import React, { useState, useEffect } from 'react'
import { Panel, Row, Col, Input, InputGroup, SelectPicker, Pagination, DatePicker } from 'rsuite'
import SearchIcon from '@rsuite/icons/Search'
import ModerationTable from '../../components/ moderation/ModerationTable'
import { moderationColumns } from '../../components/ moderation/moderationColumns'
import services from '../../services'
import './style.css'

const ModerationLogs = () => {
  const [searchValue, setSearchValue] = useState('')
  const [actionTypeFilter, setActionTypeFilter] = useState(null)
  const [dateFilter, setDateFilter] = useState(null)
  const [activePage, setActivePage] = useState(1)
  const [limit, setLimit] = useState(6)

  const [debouncedSearch, setDebouncedSearch] = useState('')

  const actionTypeOptions = [
    { label: 'All', value: null },
    { label: 'Dismiss Reported post', value: 'REPORT_DISMISSED' },
    { label: 'Delete Post', value: 'POST_REMOVED' },
    { label: 'Block User', value: 'USER_BANNED' },
    { label: 'User Unblocked', value: 'USER_UNBANNED' },
  ]

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue)
      setActivePage(1)
    }, 500)

    return () => clearTimeout(timer)
  }, [searchValue])

  useEffect(() => {
    setActivePage(1)
  }, [actionTypeFilter, dateFilter])

  const filters = {
  pageNumber: activePage, 
  pageSize: limit,
  ...(debouncedSearch && { searchTerm: debouncedSearch }),
  ...(actionTypeFilter && { actionType: actionTypeFilter }),
  ...(dateFilter && { 
    fromDate: new Date(dateFilter.getFullYear(), dateFilter.getMonth(), dateFilter.getDate()).toISOString(),
    toDate: new Date(dateFilter.getFullYear(), dateFilter.getMonth(), dateFilter.getDate(), 23, 59, 59).toISOString()
  })
}

  const { data, isLoading, isError } = services.moderation.useAdminModerationLogs(filters)

  const logs = data?.logs || []
  const totalCount = data?.totalCount || 0

  const handleChangeLimit = (dataKey) => {
    setLimit(dataKey)
    setActivePage(1)
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <h1 className="text-2xl font-bold mb-2 text-gray-900">Moderation logs</h1>
      <p className="text-gray-600 mb-6">View moderation logs here</p>
      
      {/* Total Logs Card */}
      <Row gutter={24} className="mb-8">
        <Col md={6} sm={12} xs={24}>
          <Panel className="bg-white shadow-sm" bordered>
            <div>
              <h3 className="text-lg font-semibold mb-2 text-gray-900">Total logs</h3>
              <p className="text-gray-600 text-sm mb-4">All moderation logs</p>
              <div className="flex items-center">
                <span className="text-3xl font-bold text-gray-900">
                  {totalCount}
                </span>
              </div>
            </div>
          </Panel>
        </Col>
      </Row>

      <h2 className="text-xl font-bold mb-4 text-gray-900">Moderations</h2>

      {/* Search and Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <InputGroup style={{ width: 300 }}>
          <InputGroup.Addon>
            <SearchIcon />
          </InputGroup.Addon>
          <Input 
            placeholder="Search by moderator or user"
            value={searchValue}
            onChange={(value) => setSearchValue(value)}
          />
        </InputGroup>
        
        <SelectPicker
          data={actionTypeOptions}
          value={actionTypeFilter}
          onChange={(value) => setActionTypeFilter(value)}
          cleanable={false}
          searchable={false}
          placeholder="Action type: All"
          style={{ width: 200 }}
        />

        <DatePicker
          oneTap
          placeholder="Filter by date"
          value={dateFilter}
          onChange={setDateFilter}
          style={{ width: 200 }}
        />
      </div>

      {/* Error Message */}
      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-600">Failed to load moderation logs. Please try again.</p>
        </div>
      )}

      {/* Table */}
      {!isLoading && logs.length > 0 ? (
        <ModerationTable
          data={logs}
          columns={moderationColumns}
          loading={isLoading}
        />
      ) : !isLoading && logs.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <h3 className="text-2xl font-semibold text-gray-700 mb-2">No results found!</h3>
          <p className="text-gray-500">Try another filter</p>
        </div>
      ) : isLoading ? (
        <ModerationTable
          data={[]}
          columns={moderationColumns}
          loading={true}
        />
      ) : null}

      {/* Pagination */}
      {logs.length > 0 && (
        <div className="flex justify-between items-center mt-6">
          <Pagination
            prev
            next
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="md"
            layout={['pager']}
            total={totalCount}
            limit={limit}
            activePage={activePage}
            onChangePage={setActivePage}
          />
          
          <div className="flex items-center gap-3">
            <span className="text-gray-700 font-medium">
              Logs per page
            </span>
            <SelectPicker
              data={[
                { label: '6', value: 6 },
                { label: '10', value: 10 },
                { label: '20', value: 20 },
                { label: '50', value: 50 }
              ]}
              value={limit}
              onChange={handleChangeLimit}
              cleanable={false}
              searchable={false}
              style={{ width: 80 }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default ModerationLogs